import {BaseMutation} from '../base-mutation';
import {getNodeId} from '../../utils/get-node-id';
import {insertNodeAtIndex} from '../../utils/insert-node-at-index';
import {nodeOrParentEditable} from '../../utils/node-or-parent-editable';
import {addIdToNode} from '../../utils/add-id-to-node';
import {message} from '@common/i18n/message';
import {setSelectedContext} from '@app/editor/state/set-selected-context';
import {editorState} from '@app/editor/state/editor-store';
import {executeScriptsInNode} from '../../utils/execute-scripts-in-node';

export class InsertNode extends BaseMutation {
  displayName = message('Inserted element');
  changes = {new: null, old: null};

  constructor(
    protected el: HTMLElement,
    protected newIndex: number,
    protected newParent: string,
  ) {
    super(el);
  }

  static canInsertInto(el: HTMLElement) {
    return el && !nodeOrParentEditable(el) && el.nodeName !== 'HTML';
  }

  getNodeId(): string | null {
    return this.nodeId;
  }

  protected onBeforeExecute() {
    if (!this.nodeId) {
      addIdToNode(this.el, true);
    }
    this.nodeId = getNodeId(this.el)!;
  }

  protected executeMutation(doc: Document): boolean {
    const parentEl = this.findEl(doc, this.newParent);
    if (!parentEl || !InsertNode.canInsertInto(parentEl)) {
      console.error('[InsertNode] Cannot insert - invalid parent');
      return false;
    }
    
    insertNodeAtIndex(this.el, parentEl, this.newIndex, true);
    
    const insertedNode = this.findEl(doc);
    if (insertedNode) {
      // Check for widgets that need SDK initialization
      const omeletteWidget = insertedNode.querySelector('[data-sdk="omelette"]') as HTMLElement;
      
      if (omeletteWidget) {
        // Initialize widget asynchronously
        this.initializeWidget(doc, insertedNode, omeletteWidget).catch(error => {
          console.error('[InsertNode] Failed to initialize widget:', error);
        });
      } else {
        executeScriptsInNode(insertedNode).catch(error => {
          console.error('[InsertNode] Failed to execute scripts:', error);
        });
      }
    } else {
      console.warn('[InsertNode] Could not find inserted node after insertion');
    }
    
    if (doc === this.previewDoc) {
      setSelectedContext(this.nodeId);
    }
    return true;
  }

  /**
   * Initialize a widget SDK
   */
  protected async initializeWidget(doc: Document, container: HTMLElement, widget: HTMLElement) {
    // Check if widget needs visibility fix
    const needsVisibilityFix = container.offsetHeight === 0;
    let originalMinHeight = '';
    
    if (needsVisibilityFix) {
      originalMinHeight = container.style.minHeight;
      container.style.minHeight = '100px';
    }
    
    try {
      // Wait for scripts to actually load
      await executeScriptsInNode(container);
      
      // Give SDK a moment to set up its observers
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Trigger SDK's MutationObserver
      const parent = widget.parentElement;
      const nextSibling = widget.nextSibling;
      
      if (parent) {
        parent.removeChild(widget);
        
        // Re-insert after a brief moment
        await new Promise(resolve => setTimeout(resolve, 50));
        
        if (nextSibling) {
          parent.insertBefore(widget, nextSibling);
        } else {
          parent.appendChild(widget);
        }
      }
      
      // Remove visibility fix after SDK has time to detect widget
      if (needsVisibilityFix) {
        await new Promise(resolve => setTimeout(resolve, 500));
        container.style.minHeight = originalMinHeight;
      }
      
    } catch (error) {
      console.error('[InsertNode] Error initializing widget:', error);
      // Clean up on error
      if (needsVisibilityFix) {
        container.style.minHeight = originalMinHeight;
      }
    }
  }

  protected undoMutation(doc: Document): boolean {
    const insertedEl = this.findEl(doc);
    if (insertedEl) {
      insertedEl.remove();
      if (
        doc === this.previewDoc &&
        editorState().selectedContext?.nodeId === this.nodeId
      ) {
        setSelectedContext(this.newParent);
      }
      return true;
    }
    return false;
  }
}
